import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Download, FileSpreadsheet, Activity, Shield, Heart, Users,
  ClipboardList, Target, Calendar, BarChart3, GraduationCap,
  Bell, Database, RefreshCw, CheckCircle2, FileText, File,
  AlertTriangle, TrendingUp
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const exportIcons = {
  all: Database,
  clinical: Activity,
  safety: Shield,
  'patient-experience': Heart,
  staffing: Users,
  productivity: Target,
  pips: ClipboardList,
  compliance: CheckCircle2,
  burnout: AlertTriangle,
  census: TrendingUp,
  pto: Calendar,
  training: GraduationCap,
};

const formatIcons = {
  csv: FileSpreadsheet,
  pdf: FileText,
  word: File,
};

export default function ExportReports() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [exports, setExports] = useState([]);
  
  const fetchExports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/export/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExports(data.exports);
      }
    } catch (error) {
      toast.error('Failed to load export options');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchExports();
  }, [token]);
  
  const downloadFile = async (endpoint, filename, format) => {
    const downloadKey = `${endpoint}-${format}`;
    setDownloading(downloadKey);
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success(`Downloaded ${filename}`);
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      toast.error('Error downloading file');
    } finally {
      setDownloading(null);
    }
  };
  
  const downloadReport = (reportId, format) => {
    const exportItem = exports.find(e => e.id === reportId);
    if (!exportItem) return;
    
    const endpoint = exportItem.formats[format];
    const extensions = { csv: 'csv', pdf: 'pdf', word: 'docx' };
    const filename = `${reportId}_report_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
    
    downloadFile(endpoint, filename, format);
  };
  
  const downloadAllInFormat = (format) => {
    downloadReport('all', format);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="export-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <FileSpreadsheet className="h-7 w-7 text-teal-500" />
            Export Reports
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Download your data as CSV, PDF, or Word documents
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => downloadAllInFormat('csv')} 
            disabled={downloading?.includes('csv')}
            className="border-green-300 text-green-700 hover:bg-green-50"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            All CSV
          </Button>
          <Button 
            variant="outline"
            onClick={() => downloadAllInFormat('pdf')} 
            disabled={downloading?.includes('pdf')}
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            All PDF
          </Button>
          <Button 
            onClick={() => downloadAllInFormat('word')} 
            disabled={downloading?.includes('word')}
            className="bg-gradient-to-r from-blue-500 to-indigo-500"
          >
            <File className="h-4 w-4 mr-2" />
            All Word
          </Button>
        </div>
      </div>
      
      {/* Quick Export Card */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/10 dark:to-cyan-900/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="p-4 bg-teal-100 dark:bg-teal-900/30 rounded-full">
              <Database className="h-8 w-8 text-teal-600" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-lg font-semibold">Complete Data Export</h2>
              <p className="text-muted-foreground text-sm">
                Download all your QAPI data in your preferred format. Includes all metrics, 
                PIPs, compliance data, burnout tracking, and more.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm"
                variant="outline"
                onClick={() => downloadAllInFormat('csv')}
                disabled={downloading?.includes('all-csv')}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button 
                size="sm"
                variant="outline"
                onClick={() => downloadAllInFormat('pdf')}
                disabled={downloading?.includes('all-pdf')}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <FileText className="h-4 w-4 mr-1" />
                PDF
              </Button>
              <Button 
                size="sm"
                onClick={() => downloadAllInFormat('word')}
                disabled={downloading?.includes('all-word')}
                className="bg-gradient-to-r from-blue-500 to-indigo-500"
              >
                <File className="h-4 w-4 mr-1" />
                Word
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Individual Reports */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Individual Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exports.filter(e => e.id !== 'all').map((exportItem) => {
            const Icon = exportIcons[exportItem.id] || FileSpreadsheet;
            
            return (
              <Card key={exportItem.id} className="hover:border-teal-300 transition-colors" data-testid={`export-card-${exportItem.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg flex-shrink-0">
                      <Icon className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{exportItem.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {exportItem.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Format Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-green-200 text-green-700 hover:bg-green-50"
                      onClick={() => downloadReport(exportItem.id, 'csv')}
                      disabled={downloading === `${exportItem.formats.csv}-csv`}
                      data-testid={`download-csv-${exportItem.id}`}
                    >
                      {downloading === `${exportItem.formats.csv}-csv` ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="h-3 w-3 mr-1" />
                      )}
                      CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => downloadReport(exportItem.id, 'pdf')}
                      disabled={downloading === `${exportItem.formats.pdf}-pdf`}
                      data-testid={`download-pdf-${exportItem.id}`}
                    >
                      {downloading === `${exportItem.formats.pdf}-pdf` ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <FileText className="h-3 w-3 mr-1" />
                      )}
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                      onClick={() => downloadReport(exportItem.id, 'word')}
                      disabled={downloading === `${exportItem.formats.word}-word`}
                      data-testid={`download-word-${exportItem.id}`}
                    >
                      {downloading === `${exportItem.formats.word}-word` ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <File className="h-3 w-3 mr-1" />
                      )}
                      Word
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      
      {/* Format Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-green-50 dark:bg-green-900/10 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-800">CSV Format</span>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300">
              Spreadsheet format for Excel, Google Sheets. Best for data analysis and manipulation.
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 dark:bg-red-900/10 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-red-600" />
              <span className="font-medium text-red-800">PDF Format</span>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300">
              Professional documents for printing, sharing, and archiving. Preserves formatting.
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <File className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-800">Word Format</span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Editable documents for Microsoft Word. Best for customizing reports before sharing.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
